import { useAuth0 } from "@auth0/auth0-react";
import React, { useContext, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { DarkModeContext } from "../../appContexts/DarkModeContext";
import { UserContext } from "../main/pages/_context/UserContext";
import { Icon_Admin } from "../../images/icons/Icon_Admin";
import { Icon_Home } from "../../images/icons/Icon_Home";
import { Whisper, Tooltip } from "rsuite";

export const NavigatorFC = () => {
  const { darkMode } = useContext(DarkModeContext);
  const { userRoles, loadingUserRoles, getUserProfile } =
    useContext(UserContext);
  const { isAuthenticated, user } = useAuth0();
  const location = useLocation();

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Fetch user profile when component mounts
  useEffect(() => {
    if (isAuthenticated && user) {
      getUserProfile();
    }
  }, [isAuthenticated, user]);

  // Check if user should see admin icon (hide for basic-only users)
  const shouldShowAdminIcon = () => {
    // While loading, don't show admin icon
    if (loadingUserRoles) {
      return false;
    }

    // If no roles loaded yet, don't show
    if (!userRoles || !Array.isArray(userRoles)) {
      return false;
    }

    // Hide admin icon only for users with ONLY basic role
    const isBasicOnly = userRoles.includes("basic") && userRoles.length === 1;

    /*console.log("NavigatorFC: Role check", {
      userRoles,
      isBasicOnly,
      shouldShow: !isBasicOnly,
    });*/

    return !isBasicOnly;
  };

  return (
    <NavigatorSC darkMode={darkMode}>
      <NavItem>
        <Whisper
          placement="right"
          trigger="hover"
          speaker={<Tooltip>Home</Tooltip>}
        >
          <NavLink to="/" $active={isActive("/")}>
            <IconWrapper $active={isActive("/")}>
              <Icon_Home />
            </IconWrapper>
            {isActive("/") && <ActiveIndicator />}
          </NavLink>
        </Whisper>
      </NavItem>

      {isAuthenticated && shouldShowAdminIcon() && (
        <NavItem>
          <Whisper
            placement="right"
            trigger="hover"
            speaker={<Tooltip>Admin</Tooltip>}
          >
            <NavLink to="/admin" $active={isActive("/admin")}>
              <IconWrapper $active={isActive("/admin")}>
                <Icon_Admin />
              </IconWrapper>
              {isActive("/admin") && <ActiveIndicator />}
            </NavLink>
          </Whisper>
        </NavItem>
      )}
    </NavigatorSC>
  );
};

const NavigatorSC = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const NavItem = styled.div`
  position: relative;
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  position: relative;
  text-decoration: none;
  border-radius: 14px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 46px;
  min-width: 46px;

  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(44, 90, 160, 0.2) 0%, rgba(52, 152, 219, 0.15) 100%)"
      : "transparent"};

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(44, 90, 160, 0.15) 0%,
      rgba(52, 152, 219, 0.1) 100%
    );
    transform: translateY(-2px);
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 14px;
    padding: 1px;
    background: ${({ $active }) =>
      $active
        ? "linear-gradient(135deg, rgba(44, 90, 160, 0.4), rgba(52, 152, 219, 0.2))"
        : "transparent"};
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transition: opacity 0.3s;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: ${({ $active }) => ($active ? "#2c5aa0" : "rgba(44, 90, 160, 0.6)")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  svg {
    width: 24px;
    height: 24px;
    filter: ${({ $active }) =>
      $active ? "drop-shadow(0 2px 4px rgba(44, 90, 160, 0.3))" : "none"};
  }

  ${NavLink}:hover & {
    color: #2c5aa0;
    transform: scale(1.1);
  }
`;

const ActiveIndicator = styled.div`
  position: absolute;
  bottom: -8px;
  left: 20%;
  right: 20%;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: linear-gradient(90deg, #2c5aa0 0%, #3498db 100%);
  box-shadow: 0 -2px 8px rgba(44, 90, 160, 0.4);
`;
